import { useEffect, useState, useCallback } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    Plus, CircleNotch, CheckCircle, WarningCircle, Flag, Camera, Paperclip,
} from "@phosphor-icons/react";

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";
const STATUS_CFG = {
    submitted: { label: "Submitted", chip: "chip-orange", Icon: WarningCircle },
    approved: { label: "Approved", chip: "chip-green", Icon: CheckCircle },
    flagged: { label: "Flagged", chip: "chip-gray", Icon: Flag },
};

export default function FieldLogTab({ project }) {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [rollup, setRollup] = useState(null);
    const [consumables, setConsumables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), item: "", quantity: "", unit: "", notes: "" });
    const [useCustomItem, setUseCustomItem] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rollupRes, schRes] = await Promise.all([
                endpoints.fieldLogRollup(project.id),
                endpoints.activeRateSchedule().catch(() => ({ data: null })),
            ]);
            setRollup(rollupRes.data);
            setConsumables(schRes.data?.schedule_b || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [project.id]);
    useEffect(() => { load(); }, [load]);

    function selectConsumable(e) {
        const val = e.target.value;
        if (val === "__custom__") {
            setUseCustomItem(true);
            setForm((f) => ({ ...f, item: "" }));
            return;
        }
        const match = consumables.find((c) => c.item === val);
        setForm((f) => ({ ...f, item: val, unit: match?.unit || f.unit }));
    }

    async function handlePhoto(file) {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file); fd.append("project_id", project.id); fd.append("kind", "field_log");
            const { data } = await endpoints.uploadFile(fd);
            setPhoto({ id: data.id, name: data.original_filename });
        } catch (e) { alert(formatApiError(e)); }
        finally { setUploading(false); }
    }

    async function submit() {
        if (!form.item.trim() && !photo) { setErr("Enter an item/quantity or attach a photo"); return; }
        setSubmitting(true); setErr("");
        try {
            await endpoints.createFieldLog({
                project_id: project.id, date: form.date, item: form.item.trim(),
                quantity: form.quantity ? Number(form.quantity) : null, unit: form.unit,
                notes: form.notes, photo_file_id: photo?.id || null, photo_file_name: photo?.name || "",
            });
            setForm((f) => ({ ...f, item: "", quantity: "", unit: "", notes: "" }));
            setUseCustomItem(false);
            setPhoto(null);
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSubmitting(false); }
    }

    async function review(id, status) {
        try { await endpoints.reviewFieldLog(id, { status }); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    const counts = rollup?.counts || { submitted: 0, approved: 0, flagged: 0 };

    return (
        <div className="space-y-4" data-testid="field-log-tab">
            {isManage && (
                <div className="grid grid-cols-3 gap-4">
                    <RollupCard label="Submitted (pending)" value={counts.submitted} accent="orange" />
                    <RollupCard label="Approved" value={counts.approved} accent="green" />
                    <RollupCard label="Flagged" value={counts.flagged} accent="gray" />
                </div>
            )}

            <div className="rom-card">
                <div className="rom-card-head"><h3>Log material / consumable usage</h3><span className="text-xs text-[var(--rom-text-3)]">Any crew member can submit</span></div>
                <div className="rom-card-body space-y-3">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Date *</label>
                            <input data-testid="fl-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Item</label>
                            {!useCustomItem ? (
                                <select data-testid="fl-item-select" value={form.item} onChange={selectConsumable} className={inputCls}>
                                    <option value="">Select a consumable…</option>
                                    {consumables.map((c) => <option key={c.item} value={c.item}>{c.item} ({c.unit})</option>)}
                                    <option value="__custom__">Other / custom item…</option>
                                </select>
                            ) : (
                                <div className="flex gap-1.5">
                                    <input data-testid="fl-item" value={form.item} onChange={(e) => setForm((f) => ({ ...f, item: e.target.value }))} placeholder="e.g. Antimicrobial spray" className={inputCls} autoFocus />
                                    <button type="button" data-testid="fl-item-back-to-list" onClick={() => { setUseCustomItem(false); setForm((f) => ({ ...f, item: "" })); }}
                                        className="shrink-0 px-2.5 rounded-[4px] border border-[var(--rom-border)] text-xs text-[var(--rom-text-2)] hover:border-[var(--rom-orange)] whitespace-nowrap">
                                        Pick from list
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Qty</label>
                            <div className="flex gap-1.5">
                                <input data-testid="fl-quantity" type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className={inputCls} />
                                <input data-testid="fl-unit" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="unit" className={`${inputCls} w-20`} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Notes</label>
                        <textarea data-testid="fl-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2}
                            className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none" />
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {photo ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[var(--rom-border-strong)] text-xs text-[var(--rom-text-2)]" data-testid="fl-photo-attached">
                                <Camera size={13} weight="bold" /> {photo.name}
                            </span>
                        ) : (
                            <>
                                <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-dashed border-[var(--rom-border-strong)] text-xs cursor-pointer hover:border-[var(--rom-orange)] text-[var(--rom-text-2)]" data-testid="fl-take-photo">
                                    {uploading ? <CircleNotch size={13} className="animate-spin" /> : <Camera size={13} weight="bold" />} {uploading ? "Uploading…" : "Take photo"}
                                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handlePhoto(f); }} />
                                </label>
                                <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-dashed border-[var(--rom-border-strong)] text-xs cursor-pointer hover:border-[var(--rom-orange)] text-[var(--rom-text-2)]" data-testid="fl-photo-upload">
                                    {uploading ? <CircleNotch size={13} className="animate-spin" /> : <Paperclip size={13} weight="bold" />} {uploading ? "Uploading…" : "Upload photo"}
                                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handlePhoto(f); }} />
                                </label>
                            </>
                        )}
                        {photo && <button onClick={() => setPhoto(null)} className="text-xs text-red-500 hover:underline" data-testid="fl-photo-clear">Remove photo</button>}
                        {!form.item.trim() && photo && (
                            <span className="text-[11px] text-amber-700 flex items-center gap-1"><WarningCircle size={12} weight="bold" /> Photo-only — will be flagged for manual review</span>
                        )}
                    </div>
                    {err && <div className="text-xs text-red-600" data-testid="fl-error">{err}</div>}
                    <button data-testid="fl-submit" onClick={submit} disabled={submitting} className="btn btn-primary">
                        {submitting ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Log entry
                    </button>
                </div>
            </div>

            <div className="rom-card">
                <div className="rom-card-head"><h3>Field log entries</h3></div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Date</th><th>Submitted by</th><th>Item</th><th className="numeric">Qty</th><th>Photo</th><th>Status</th>{isManage && <th></th>}</tr></thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isManage ? 7 : 6} className="text-center py-8 text-[var(--rom-text-3)]">Loading…</td></tr>
                            ) : (rollup?.entries || []).length === 0 ? (
                                <tr><td colSpan={isManage ? 7 : 6} className="text-center py-8 text-[var(--rom-text-3)]" data-testid="fl-empty">No field log entries yet.</td></tr>
                            ) : rollup.entries.map((e) => {
                                const cfg = STATUS_CFG[e.status] || STATUS_CFG.submitted;
                                return (
                                    <tr key={e.id} data-testid={`fl-row-${e.id}`}>
                                        <td className="font-mono text-xs">{e.date}</td>
                                        <td className="text-[var(--rom-text-2)]">{e.employee_name || e.submitted_by_name}</td>
                                        <td>{e.item || <span className="text-[var(--rom-text-3)] italic">no item entered</span>}</td>
                                        <td className="numeric">{e.quantity != null ? `${e.quantity} ${e.unit || ""}` : "—"}</td>
                                        <td>{e.photo_file_id ? (
                                            <a href={endpoints.fileDownloadUrl(e.photo_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--rom-orange-dark)] hover:underline text-xs"><Paperclip size={11} weight="bold" /> view</a>
                                        ) : "—"}</td>
                                        <td>
                                            <span className={`chip ${cfg.chip}`}><cfg.Icon size={11} weight="bold" /> {cfg.label}</span>
                                            {e.review_note && <div className="text-[10px] text-[var(--rom-text-3)] mt-0.5 max-w-[180px]">{e.review_note}</div>}
                                        </td>
                                        {isManage && (
                                            <td>
                                                {e.status !== "approved" && (
                                                    <button onClick={() => review(e.id, "approved")} data-testid={`fl-approve-${e.id}`} className="text-xs text-[var(--rom-green)] font-semibold hover:underline mr-2">Approve</button>
                                                )}
                                                {e.status !== "flagged" && (
                                                    <button onClick={() => review(e.id, "flagged")} data-testid={`fl-flag-${e.id}`} className="text-xs text-amber-600 font-semibold hover:underline">Flag</button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function RollupCard({ label, value, accent }) {
    const color = accent === "orange" ? "var(--rom-orange-dark)" : accent === "green" ? "var(--rom-green)" : "var(--rom-text-2)";
    return (
        <div className="rom-card"><div className="rom-card-body">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">{label}</div>
            <div className="font-display font-black text-3xl tracking-tight mt-1" style={{ color }}>{value}</div>
        </div></div>
    );
}
